import { ErrorReportRequest } from './error-reporting.contracts';
import { AppError } from '../core';
import { ErrorReportAttachment } from './error-report-operation';
import { ErrorReportContext } from './error-report-context';
import { redactErrorContext, redactText } from './error-report-redaction';

/** User-supplied fields collected by the report form. */
export interface ErrorReportFormInput {
  /** Optional free-text description from the user. */
  comment?: string;
  /** Optional contact e-mail. */
  email?: string;
  isPrivate?: boolean;
  files?: readonly ErrorReportAttachment[];
  reportId?: string;
}

/**
 * Maps a captured (already redacted) {@link ErrorReportContext} plus the user's
 * form input into the {@link ErrorReportRequest} the backend expects.
 *
 * User text, sanitized diagnostics and binary files are submitted separately
 * for the backend to include in the same forum topic.
 */
export function buildErrorReportRequest(
  context: ErrorReportContext,
  input: ErrorReportFormInput = {}
): ErrorReportRequest {
  context = redactErrorContext(context);
  const kind = context.kind ?? 'error';
  const files = input.files ?? context.operation?.files ?? [];
  const diagnostics = kind === 'feedback' ? '' : buildErrorReportDiagnostics(context, files);
  return {
    ReportId: input.reportId ?? crypto.randomUUID(),
    Kind: kind,
    App: context.appName,
    Title: kind === 'feedback' ? 'Feedback' : 'Error report',
    Message: redactText(input.comment?.trim() ?? ''),
    Detail: diagnostics,
    OriginalUrl: context.pageUrl,
    Email: input.email?.trim() || undefined,
    IsPrivatePost: input.isPrivate !== false,
    Files: files,
  };
}

/** Builds the diagnostic context for the forum post; never includes image bytes. */
export function buildErrorReportDiagnostics(context: ErrorReportContext, selected: readonly ErrorReportAttachment[] = context.operation?.files ?? []): string {
  context = redactErrorContext(context);
  const lines: Array<string | null> = [];

  lines.push(
    `Severity: ${context.severity}`,
    `Page: ${context.pageUrl}`,
    context.routePath ? `Route: ${context.routePath}` : null,
    `App: ${context.appName}${context.appVersion ? ` v${context.appVersion}` : ''}`,
    `Environment: ${context.isProduction ? 'production' : 'development'}`,
    `Online: ${context.online}`,
    `User agent: ${context.userAgent}`,
    `Time: ${context.timestamp}`
  );

  if (context.operation) {
    const { files = [], ...operation } = context.operation;
    lines.push(`Operation: ${JSON.stringify(operation)}`);
    if (files.length) lines.push(`File inputs: ${JSON.stringify(files.map((file, index) => ({
      input: file.inputIndex ?? index + 1,
      name: redactText(file.name),
      uploadIndex: selected.findIndex(candidate => candidate.blob === file.blob && candidate.name === file.name),
    })))}`);
  }

  for (const entry of context.errors) {
    for (let error: AppError | undefined = entry; error; error = error.InnerException) {
      lines.push('', `${error.Type}: ${error.ExceptionMessage}`, error.Details);
    }
  }

  const diagnostics = lines.filter((line): line is string => line !== null).join('\n');
  return diagnostics.length > 50_000 ? `${diagnostics.slice(0, 49_980)}\n[Truncated]` : diagnostics;
}
